import { Component, ElementRef, HostListener, Inject, ViewChild } from "@angular/core";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA as MAT_DIALOG_DATA } from "@angular/material/dialog";
import { DesktopErrorHandler } from "../../services/errorHandler.service";

@Component({
  selector: 'search-dialog',
  templateUrl: 'search-dialog.component.html',
  styleUrls: ["./search-dialog.component.scss"]
})
export class SearchDialog<T> {
  @ViewChild('search', { static: false }) set searchInput(searchInput: ElementRef) {
    this._searchInput = searchInput;
    if(this.shouldAutofocus && searchInput != null) {
      this.shouldAutofocus = false;
      searchInput.nativeElement.focus();
    }
  }

  get searchInput(): ElementRef {
    return this._searchInput;
  }

  set working(value: boolean) {
    this._working = value;
    this.dialogRef.disableClose = value;
  }

  get working(): boolean {
    return this._working;
  }

  _working: boolean;
  _searchInput: ElementRef;
  shouldAutofocus: boolean;
  searchInputValue: string;
  searchTerm: string;
  loadingSearchResults: { [term: string]: boolean };
  searchResults: T[];
  selectedSearchResult: T;
  searchPageNum: number;
  searchPageSize: number;
  searchInfiniteScrollDisabled: boolean;
  customLoading: boolean;
  customText: string;
  suggestions: T[];

  constructor(
    private errorHandler: DesktopErrorHandler,
    public dialogRef: MatDialogRef<SearchDialog<T>>,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: SearchDialogData<T>) {
    this.working = false;
    this.shouldAutofocus = false;
    this.searchInputValue = this.data.term || '';
    this.searchTerm = '';
    this.loadingSearchResults = {};
    this.searchPageNum = 0;
    this.searchPageSize = (this.data.pageSize != null && this.data.pageSize > 0) ? this.data.pageSize : 20;
    this.searchInfiniteScrollDisabled = true;
    this.customLoading = false;
    this.suggestions = data.suggestions;
    if(this.data.term)
      this.searchInputChanged(this.data.term);
    else
      this.clearSearchResults();
  }

  clearSearchResults() {
    this.searchResults = this.suggestions || null;
  }

  searchInputChanged(term: string = '') {
    term = term.trim();
    if(term == this.searchTerm || this.loadingSearchResults[term])
      return;
    this.selectedSearchResult = null;
    this.customText = null;
    if(term == '') {
      this.searchTerm = term;
      this.clearSearchResults()
      return;
    }
    this.getNextSearchPage(term, true);
  }

  getNextSearchPage(term: string, first: boolean = false) {
    this.loadingSearchResults[term] = true;
    this.searchInfiniteScrollDisabled = true;
    if(first) {
      this.searchTerm = term;
      this.searchResults = [];
      this.searchPageNum = 0;
      if(this.data.hasExact) {
        this.customLoading = true;
        Promise.resolve(this.data.hasExact(term)).then((value: boolean) => {
          if(this.searchTerm != term)
            return;
          if(!value)
            this.customText = term;
          this.customLoading = false;
        }, (error: any) => {
          if(this.searchTerm != term)
            return;
          this.customLoading = false;
          this.errorHandler.handle(error, this.data.exactErrorContext || 'result');
        });
      } else
        this.customLoading = false;
    }
    Promise.resolve(this.data.getPage(term, this.searchPageNum++, this.searchPageSize)).then((results: T[]) => {
      if(this.searchTerm == term) {
        if(this.searchResults == null)
          this.searchResults = [];
        this.searchResults = this.searchResults.concat(results);
      }
      this.searchInfiniteScrollDisabled = results.length < this.searchPageSize;
      this.loadingSearchResults[term] = false;
    }, (error: any) => {
      this.loadingSearchResults[term] = false;
      this.errorHandler.handle(error, this.data.searchErrorContext || 'results', DesktopErrorHandler.SEARCH_MESSAGES);
    });
  }

  shouldShowCustom(): boolean {
    return this.data.hasExact && !this.customLoading && this.customText && (this.hasSearchResults() || !this.isLoadingSearchResults())
      && (!this.data.customIsValid || this.data.customIsValid(this.customText));
  }

  isLoadingSearchResults(): boolean {
    return this.loadingSearchResults[this.searchTerm] == true;
  }

  hasSearchResults(): boolean {
    return this.searchResults && this.searchResults.length > 0;
  }

  selectSearchResult(result: T) {
    if(this.working)
      return;
    this.selectedSearchResult = result;
  }

  searchResultIsSelected(result: T): boolean {
    return this.selectedSearchResult && this.data.resultsMatch(result, this.selectedSearchResult);
  }

  getCustom(): T {
    return this.data.stringToResult ? this.data.stringToResult(this.customText) : null;
  }

  selectCustom() {
    this.selectSearchResult(this.getCustom());
  }

  customIsSelected(): boolean {
    return this.selectedSearchResult && this.data.textIsResult && this.data.textIsResult(this.customText, this.selectedSearchResult);
  }

  canUseSearchResult(): boolean {
    return !this.working && this.selectedSearchResult != null;
  }

  useSearchResult() {
    if(!this.canUseSearchResult())
      return;
    this.working = true;
    const result: T = this.selectedSearchResult;
    if(this.data.handleResult) {
      Promise.resolve(this.data.handleResult(result)).then(() => {
        this.dialogRef.close(result);
      }, () => {
        this.working = false;
      });
    } else
      this.dialogRef.close(result);
  }

  getSelectedSearchResultIndex(searchResults: T[]): number {
    if(!(this.selectedSearchResult && searchResults))
      return -1;
    for(let i = 0; i < searchResults.length; i++) {
      if(this.data.resultsMatch(searchResults[i], this.selectedSearchResult))
        return i;
    }
    return -1;
  }

  selectNextSearchResult() {
    const searchResults: T[] = (this.shouldShowCustom() ? [this.getCustom()] : []).concat(this.searchResults);
    if(!searchResults || searchResults.length == 0)
      return;
    const index = this.getSelectedSearchResultIndex(searchResults);
    if(index < 0)
      this.selectSearchResult(searchResults[0]);
    else
      this.selectSearchResult(searchResults[(index + 1) % searchResults.length]);
  }

  selectPreviousSearchResult() {
    const searchResults: T[] = (this.shouldShowCustom() ? [this.getCustom()] : []).concat(this.searchResults);
    if(!searchResults || searchResults.length == 0)
      return;
    const index = this.getSelectedSearchResultIndex(searchResults);
    if(index < 0)
      this.selectSearchResult(searchResults[searchResults.length - 1]);
    else
      this.selectSearchResult(searchResults[index == 0 ? searchResults.length - 1 : index - 1]);
  }

  searchResultsScrolled() {
    this.getNextSearchPage(this.searchTerm);
  }

  @HostListener('window:keydown', ['$event'])
  handleKeydown(event: any) {
    if(this.dialog.openDialogs?.length > 0 && this.dialog.openDialogs[this.dialog.openDialogs.length - 1] != this.dialogRef)
      return;
    switch(event.key) {
      case 'ArrowUp':
        event.preventDefault();
        this.selectPreviousSearchResult();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.selectNextSearchResult();
        break;
      case 'Enter':
        event.preventDefault();
        if(this.selectedSearchResult != null)
          this.useSearchResult();
        break;
      default:
    }
  }
}

export interface SearchDialogData<T> {
  term?: string;
  promptText: string;
  placeholderText?: string;
  noResultsText?: string;
  confirmText?: string;
  suggestions?: T[];
  exactErrorContext?: string;
  searchErrorContext?: string;
  pageSize?: number;
  getText: (result: T) => string;
  getImage?: (result: T) => string;
  hasExact?: (term: string) => boolean | Promise<boolean>;
  getPage: (term: string, page: number, size: number) => T[] | Promise<T[]>;
  handleResult?: (result: T) => void | Promise<void>;
  stringToResult?: (str: string) => T;
  customIsValid?: (str: string) => boolean;
  resultsMatch: (a: T, b: T) => boolean;
  textIsResult?: (text: string, result: T) => boolean;
}